import React, { useState } from "react";
import { motion } from "framer-motion";
import { Home, Ruler, Calendar, Bed, Bath, Flag, Check } from "lucide-react";
import { fmtNum, fmtUSD } from "../../lib/format";

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-3 border-b border-brand-hairline last:border-0">
    <Icon className="h-4 w-4 text-brand-gold shrink-0" />
    <span className="text-sm text-brand-stone flex-1">{label}</span>
    <span className="font-mono text-sm font-medium text-brand-ink">{value}</span>
  </div>
);

export default function PropertyConfirm({ property, onConfirm, onFlag }) {
  const [flagging, setFlagging] = useState(false);
  const [note, setNote] = useState("");
  const p = property;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="rounded-2xl bg-white border border-brand-hairline p-6 sm:p-8 shadow-[0_4px_20px_rgba(27,59,43,0.04)]" data-testid="property-confirm-card">
      <span className="eyebrow">Step 2 · Confirm details</span>
      <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-brand-ink">{p.address.normalized}</h2>
      <p className="mt-1 text-sm text-brand-stone">{p.property_type}{p.address.unit ? ` · Unit ${p.address.unit}` : ""}</p>

      <div className="mt-6 grid sm:grid-cols-2 gap-x-10">
        <Row icon={Bed} label="Bedrooms" value={p.beds} />
        <Row icon={Bath} label="Bathrooms" value={p.baths} />
        <Row icon={Ruler} label="Living area" value={p.living_area_sqft ? `${fmtNum(p.living_area_sqft)} sq ft` : "—"} />
        <Row icon={Home} label="Lot size" value={p.lot_size_sqft ? `${fmtNum(p.lot_size_sqft)} sq ft` : "Not available"} />
        <Row icon={Calendar} label="Year built" value={p.year_built || "Not available"} />
        <Row icon={Home} label="Previous sale" value={p.previous_sale ? `${fmtUSD(p.previous_sale.price)} · ${p.previous_sale.date || ""}`.replace(/ · $/, "") : "Not available"} />
      </div>

      {flagging ? (
        <div className="mt-6 rounded-xl bg-brand-sand p-4">
          <label className="text-sm font-medium text-brand-ink" htmlFor="flag-note">What's incorrect?</label>
          <textarea id="flag-note" data-testid="property-flag-note" value={note} onChange={(e) => setNote(e.target.value)}
            className="mt-2 w-full rounded-lg border border-brand-hairline p-3 text-sm outline-none focus:ring-1 focus:ring-brand-emerald" rows={3}
            placeholder="e.g., The living area is actually 3,100 sq ft." />
          <p className="mt-2 text-xs text-brand-stone">We review flagged details manually. Source records are not automatically overwritten.</p>
          <div className="mt-3 flex gap-2">
            <button onClick={() => { onFlag(note); setFlagging(false); setNote(""); }} data-testid="property-flag-submit"
              className="rounded-lg bg-brand-emerald px-4 py-2 text-sm text-brand-cream hover:bg-brand-emeraldHover">Submit</button>
            <button onClick={() => setFlagging(false)} className="rounded-lg border border-brand-hairline px-4 py-2 text-sm">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <button onClick={onConfirm} data-testid="property-confirm-button"
            className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-brand-emerald px-6 py-3.5 font-semibold text-brand-cream hover:bg-brand-emeraldHover transition-colors">
            <Check className="h-4 w-4" /> Confirm &amp; See My Value
          </button>
          <button onClick={() => setFlagging(true)} data-testid="property-flag-button"
            className="flex items-center justify-center gap-2 rounded-xl border border-brand-hairline px-6 py-3.5 text-sm text-brand-stone hover:border-brand-emerald hover:text-brand-emerald transition-colors">
            <Flag className="h-4 w-4" /> Flag incorrect info
          </button>
        </div>
      )}
    </motion.div>
  );
}
